import { Suspense, useEffect, useRef, useState, type ReactNode } from 'react';

interface DeferredSectionProps {
  children: ReactNode;
  placeholderClassName: string;
  rootMargin?: string;
  forceRender?: boolean;
}

export function DeferredSection({
  children,
  placeholderClassName,
  rootMargin = '1400px 0px',
  forceRender = false
}: DeferredSectionProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [shouldRender, setShouldRender] = useState(forceRender);

  useEffect(() => {
    if (forceRender) {
      setShouldRender(true);
      return undefined;
    }

    const container = containerRef.current;
    if (!container || shouldRender) return undefined;

    if (!('IntersectionObserver' in window)) {
      setShouldRender(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setShouldRender(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );

    observer.observe(container);
    return () => observer.disconnect();
  }, [forceRender, rootMargin, shouldRender]);

  const placeholder = (
    <div className={placeholderClassName} aria-hidden="true" />
  );

  return (
    <div ref={containerRef}>
      {shouldRender ? (
        <Suspense fallback={placeholder}>{children}</Suspense>
      ) : (
        placeholder
      )}
    </div>
  );
}
